<script setup>
// V1 Admin Intelligence Bridge (Part 7) — read-only dashboard over
// Nexceo::Ai::WorkforceAnalyticsService#summary. No aggregation logic here.
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import workforceAnalyticsAPI from 'dashboard/api/nexceo/workforceAnalytics';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const error = ref('');
const summary = ref(null);
const sinceDays = ref(30);

async function load() {
  isLoading.value = true;
  error.value = '';
  try {
    const since = new Date(
      Date.now() - sinceDays.value * 24 * 60 * 60 * 1000
    ).toISOString();
    const { data } = await workforceAnalyticsAPI.get(since);
    summary.value = data;
  } catch {
    error.value = 'Failed to load workforce analytics.';
  } finally {
    isLoading.value = false;
  }
}

function back() {
  router.push(accountScopedRoute('nexceo_ai_workforce'));
}

const STAT_CARDS = computed(() => {
  const s = summary.value;
  if (!s) return [];
  return [
    {
      label: 'Tasks Completed',
      value: s.work_completed,
      icon: 'i-lucide-check-circle-2',
      color: '#10b981',
    },
    {
      label: 'Tasks Failed',
      value: s.work_failed,
      icon: 'i-lucide-x-circle',
      color: '#ef4444',
      danger: true,
    },
    {
      label: 'Capability Transfers',
      value: s.transfers,
      icon: 'i-lucide-shuffle',
      color: '#8b5cf6',
    },
    {
      label: 'AI Utilization',
      value: `${s.ai_utilization_percent ?? 0}%`,
      icon: 'i-lucide-cpu',
      color: '#6366f1',
    },
    {
      label: 'Avg Response',
      value: s.average_response_ms ? `${s.average_response_ms}ms` : '—',
      icon: 'i-lucide-timer',
      color: '#f59e0b',
    },
    {
      label: 'Human Takeovers',
      value: s.human_interventions,
      icon: 'i-lucide-user-check',
      color: '#f97316',
    },
    {
      label: 'Successful Handoffs',
      value: s.successful_handoffs,
      icon: 'i-lucide-badge-check',
      color: '#10b981',
    },
    {
      label: 'Satisfaction Proxy',
      value: s.customer_satisfaction_proxy ?? '—',
      icon: 'i-lucide-smile',
      color: '#3b82f6',
    },
    {
      label: 'Follow-up Success',
      value: `${s.follow_up_success_rate ?? 0}%`,
      icon: 'i-lucide-repeat',
      color: '#0ea5e9',
    },
    {
      label: 'Documents Shared',
      value: s.documents_shared,
      icon: 'i-lucide-file-text',
      color: '#64748b',
    },
    {
      label: 'Google Meets',
      value: s.google_meets,
      icon: 'i-lucide-video',
      color: '#8b5cf6',
    },
  ];
});

function entries(obj) {
  return Object.entries(obj || {}).sort(([, a], [, b]) => b - a);
}

function formatLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function barWidth(count, list) {
  if (!count) return '0%';
  const max = Math.max(...list.map(([, v]) => v), 1);
  return `${Math.max((count / max) * 100, 4)}%`;
}

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
        @click="back"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <span class="i-lucide-bar-chart-3 size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">
        AI Workforce Analytics
      </h1>

      <div class="ml-auto flex items-center gap-2">
        <select
          v-model.number="sinceDays"
          class="appearance-none text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
          @change="load"
        >
          <option :value="7">Last 7 days</option>
          <option :value="30">Last 30 days</option>
          <option :value="90">Last 90 days</option>
        </select>
        <button
          :disabled="isLoading"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:opacity-40"
          title="Refresh"
          @click="load"
        >
          <span
            :class="isLoading ? 'animate-spin' : ''"
            class="i-lucide-refresh-cw size-4"
          />
        </button>
      </div>
    </div>

    <div
      v-if="error"
      class="px-4 py-2 text-sm text-ruby-600 dark:text-ruby-400"
    >
      {{ error }}
    </div>

    <div
      v-if="isLoading && !summary"
      class="flex-1 flex items-center justify-center"
    >
      <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
    </div>

    <div v-else-if="summary" class="flex-1 overflow-auto p-5 space-y-5">
      <!-- Stat cards -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div
          v-for="card in STAT_CARDS"
          :key="card.label"
          class="flex items-center gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div
            class="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
            :style="{ backgroundColor: card.color + '18' }"
          >
            <span
              class="size-4"
              :class="card.icon"
              :style="{ color: card.color }"
            />
          </div>
          <div class="min-w-0">
            <p class="text-[11px] text-n-slate-7 leading-tight truncate">
              {{ card.label }}
            </p>
            <p
              class="text-xl font-bold leading-tight mt-0.5 tabular-nums truncate"
              :class="
                card.danger && Number(card.value) > 0
                  ? 'text-ruby-500'
                  : 'text-n-slate-12'
              "
            >
              {{ card.value ?? '—' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Distributions grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="[title, key] in [
            ['Decision Journey Distribution', 'decision_journey_distribution'],
            ['Buying Signals Distribution', 'buying_score_distribution'],
            ['Sales Strategies', 'sales_strategies'],
            ['Conversation Goals', 'conversation_goals'],
            ['Playbooks Used', 'playbooks_used'],
            ['Capability Usage', 'capability_usage'],
            ['Site Visits', 'site_visits'],
          ]"
          :key="key"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h2
            class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
          >
            {{ title }}
          </h2>
          <div
            v-if="!entries(summary[key]).length"
            class="text-xs text-n-slate-7"
          >
            No data yet.
          </div>
          <div v-else class="flex flex-col gap-2">
            <div v-for="[label, count] in entries(summary[key])" :key="label">
              <div class="flex items-center justify-between text-xs mb-0.5">
                <span class="text-n-slate-11 capitalize">{{
                  formatLabel(label)
                }}</span>
                <span class="text-n-slate-7 font-medium">{{ count }}</span>
              </div>
              <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
                <div
                  class="h-full rounded-full bg-woot-500"
                  :style="{ width: barWidth(count, entries(summary[key])) }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
